import { useEffect, useRef, useCallback } from 'react';

const PORTAL_SELECTOR = '[role="presentation"], [role="dialog"]';

const isScrollable = (node) => {
  const { overflowY } = getComputedStyle(node);
  return [ 'auto', 'scroll' ].includes(overflowY) && node.scrollHeight > node.clientHeight;
};

const findScrollableParent = (el) => {
  let node = el?.parentElement;

  while (node && node !== document.body) {
    if (isScrollable(node)) return node;
    node = node.parentElement;
  }

  return null;
};

const findScrollableInPortal = (el) => {
  const portal = el?.closest(PORTAL_SELECTOR);
  if (!portal) return null;

  return [ ...portal.querySelectorAll('*') ].find(isScrollable) ?? null;
};

const useScrollRedirect = () => {
  const scrollableRef = useRef(null);

  const handleWheel = useCallback((e) => {
    const scrollable = scrollableRef.current;
    if (!scrollable) return;

    // Inside the main scrollable body
    if (scrollable.contains(e.target)) return;

    // Inside another scrollable container (e.g. billing editor dialog)
    if (findScrollableParent(e.target)) return;

    // Redirect scroll to the dialog's scrollable area, or fall back to main body
    const target = findScrollableInPortal(e.target) ?? scrollable;
    e.preventDefault();
    target.scrollTop += e.deltaY;
  }, []);

  useEffect(() => {
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => window.removeEventListener('wheel', handleWheel);
  }, [ handleWheel ]);

  return scrollableRef;
};

export default useScrollRedirect;
